import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ArrowDown, ArrowUp, ListOrdered, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { vendorLabel } from '@/lib/providers/integration-state';
import { TOGGLE_REASON_MAX } from '@/lib/providers/integration-toggle';
import { inUseSummary, leaderWhy, routingKey } from '@/lib/providers/provider-routing';
import type { RoutingStrategy, RoutingView } from '@/lib/providers/types';
import { StrategyChip } from './provider-table';
import { useRoutingDraft, type RoutingDraftApi } from './use-routing-draft';

/**
 * §14.5 — "Order of use" on the redesigned Proveedores: which provider of a
 * service serves a call, in what order, combined how. The decision lives in
 * apps/integrations and every app routes its next call by it
 * (`integrations-tds.md` §14). Its controls now live where the operator looks:
 *
 *  · {@link RoutingStrategyControl} — the strategy chip in the card header; a
 *    compact select when the operator may change it.
 *  · {@link RoutingBar} — one line under the header: what is in use today (read
 *    off the SAVED view, never the draft), whether that is the platform
 *    default, and — once something is pending — the reason + Discard / Save bar.
 *  · {@link RoutingMemberControls} — in each provider's row detail: "participates"
 *    and move up / down.
 *
 * THE AIRLINE ALWAYS WINS (owner decision 2026-10-29): when the view names a
 * `leader` (the airline provider), the bar says so, its row reads locked — first
 * on flight information — and no control offers an order that would put another
 * source above it or let one answer in its place. Its switch, on the airline
 * provider's card, is what takes it out.
 */


/**
 * One draft per routed key of a card. Hooks cannot run in a loop, so each key
 * gets a component level of its own, and the last level hands every draft to
 * the card.
 */
export function RoutingDrafts({
  views,
  children,
}: {
  views: RoutingView[];
  children: (drafts: RoutingDraftApi[]) => ReactNode;
}) {
  const first = views[0];
  if (!first) return <>{children([])}</>;
  return <DraftLevel key={routingKey(first)} views={views} index={0} acc={[]} render={children} />;
}

function DraftLevel({
  views,
  index,
  acc,
  render,
}: {
  views: RoutingView[];
  index: number;
  acc: RoutingDraftApi[];
  render: (drafts: RoutingDraftApi[]) => ReactNode;
}) {
  const api = useRoutingDraft(views[index] as RoutingView);
  const drafts = [...acc, api];
  const next = views[index + 1];
  if (!next) return <>{render(drafts)}</>;
  return <DraftLevel key={routingKey(next)} views={views} index={index + 1} acc={drafts} render={render} />;
}

function capabilityLabel(t: TFunction<'network'>, view: Pick<RoutingView, 'capability'>): string {
  return t(`providers.routing.capability.${view.capability}`, { defaultValue: view.capability });
}

/** "In use today: Cabify" — what the next call does, from the SAVED view. */
function inUseText(t: TFunction<'network'>, view: RoutingView): string {
  const name = (vendor: string) => vendorLabel(t, view.category, vendor);
  const summary = inUseSummary(view);
  if (summary.kind === 'first') return t('providers.routing.inUse.first', { name: name(summary.vendor) });
  if (summary.kind === 'parallel') {
    return t('providers.routing.inUse.parallel', { names: summary.vendors.map(name).join(' · ') });
  }
  if (summary.kind === 'rotation') {
    return t('providers.routing.inUse.rotation', { names: summary.vendors.map(name).join(' · ') });
  }
  if (summary.kind === 'eachOwn') {
    return t('providers.routing.inUse.eachOwn', { names: summary.vendors.map(name).join(' · ') });
  }
  return t('providers.routing.inUse.none');
}

/**
 * The header chip: the strategy in force (or being drafted). An editable select
 * for a supervisor when the key allows more than one strategy; a plain chip
 * otherwise. `multi` prefixes the capability when a card routes several keys.
 */
export function RoutingStrategyControl({ api, multi }: { api: RoutingDraftApi; multi: boolean }) {
  const { t } = useTranslation('network');
  const id = useId();
  const capability = capabilityLabel(t, api.view);
  const hint = t(`providers.routing.strategyHint.${api.draft.strategy}`);
  const label = (s: RoutingStrategy) => t(`providers.routing.strategy.${s}`);

  if (!api.canEdit || api.options.length < 2) {
    return (
      <StrategyChip title={hint} testId={`routing-strategy-${api.key}`}>
        {multi ? `${capability}: ${label(api.draft.strategy)}` : label(api.draft.strategy)}
      </StrategyChip>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <label htmlFor={id} className={multi ? 'text-muted-foreground' : 'sr-only'}>
        {multi ? capability : t('providers.routing.strategyFor', { capability })}
      </label>
      <select
        id={id}
        value={api.draft.strategy}
        disabled={!api.editable}
        onChange={(e) => api.setStrategy(e.target.value as RoutingStrategy)}
        title={hint}
        className="min-h-11 rounded-md border border-input bg-background px-2 text-xs disabled:cursor-not-allowed disabled:opacity-70"
        data-testid={`routing-strategy-${api.key}`}
      >
        {api.options.map((s) => (
          <option key={s} value={s}>
            {label(s)}
          </option>
        ))}
      </select>
    </span>
  );
}

/** One line per routed key under the card header, plus the save bar when pending. */
export function RoutingBar({ api }: { api: RoutingDraftApi }) {
  const { t } = useTranslation('network');
  const headingId = useId();
  const { view, key } = api;

  return (
    <section
      aria-labelledby={headingId}
      className="space-y-2 border-b bg-muted/30 px-4 py-2 text-xs"
      data-testid={`routing-panel-${key}`}
      data-source={view.source}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <h3 id={headingId} className="flex items-center gap-1.5 font-semibold">
          <ListOrdered className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          {t('providers.routing.title', { capability: capabilityLabel(t, view) })}
        </h3>
        <span aria-hidden className="text-muted-foreground">
          ·
        </span>
        <span className="font-medium" data-testid={`routing-in-use-${key}`}>
          {inUseText(t, view)}
        </span>
        <span aria-hidden className="text-muted-foreground">
          ·
        </span>
        <span className="text-muted-foreground" data-testid={`routing-source-${key}`}>
          {t(`providers.routing.source.${view.source}`)}
        </span>
        {view.leader ? (
          <>
            <span aria-hidden className="text-muted-foreground">
              ·
            </span>
            <span
              className="inline-flex items-center gap-1 font-medium"
              title={leaderWhy(t, view, view.leader)}
              data-testid={`routing-leader-${key}`}
            >
              <Lock className="h-3 w-3" aria-hidden />
              {t('providers.routing.leader.badge')}
            </span>
          </>
        ) : null}
        {!api.canEdit ? (
          <span className="text-muted-foreground" data-testid={`routing-read-only-${key}`}>
            {' — '}
            {t('providers.routing.readOnly')}
          </span>
        ) : null}
      </div>

      {api.canEdit && api.dirty ? (
        <div className="space-y-2" data-testid={`routing-actions-${key}`}>
          <p className="text-muted-foreground" data-testid={`routing-strategy-hint-${key}`}>
            {t('providers.routing.pending')} {t(`providers.routing.strategyHint.${api.draft.strategy}`)}
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="min-w-0 flex-1 basis-64">
              <span className="mb-1 block text-muted-foreground">{t('providers.routing.reasonLabel')}</span>
              <input
                type="text"
                value={api.reason}
                maxLength={TOGGLE_REASON_MAX}
                disabled={api.busy}
                onChange={(e) => api.setReason(e.target.value)}
                placeholder={t('providers.routing.reasonPlaceholder')}
                className="h-11 w-full rounded-md border border-input bg-background px-2 text-xs"
                data-testid={`routing-reason-${key}`}
              />
            </label>
            <Button
              type="button"
              variant="ghost"
              className="h-11"
              disabled={api.busy}
              onClick={api.discard}
              data-testid={`routing-discard-${key}`}
            >
              {t('providers.routing.discard')}
            </Button>
            <Button
              type="button"
              className="h-11"
              disabled={api.busy}
              onClick={api.save}
              data-testid={`routing-save-${key}`}
            >
              {api.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
              {api.busy ? t('providers.routing.saving') : t('providers.routing.save')}
            </Button>
          </div>
        </div>
      ) : null}

      {api.notice === 'conflict' ? (
        <p role="status" className="text-amber-700 dark:text-amber-400" data-testid={`routing-conflict-${key}`}>
          {t('providers.routing.conflict')}
        </p>
      ) : api.notice === 'saved' && !api.dirty ? (
        <p role="status" className="text-emerald-700 dark:text-emerald-400" data-testid={`routing-saved-${key}`}>
          {t('providers.routing.saved')}
        </p>
      ) : null}
      {api.errorCode ? (
        <p role="alert" className="text-destructive" data-testid={`routing-error-${key}`}>
          {t(`providers.routing.error.${api.errorCode}`)}
        </p>
      ) : null}
    </section>
  );
}

/**
 * In a provider's row detail: its place in one key's order, "participates", and
 * move up / down (labelled buttons, reachable from the keyboard). Renders
 * nothing when the vendor is not a member of that key.
 */
export function RoutingMemberControls({ api, vendor }: { api: RoutingDraftApi; vendor: string }) {
  const { t } = useTranslation('network');
  const { draft, key, view } = api;
  const index = draft.members.findIndex((m) => m.vendor === vendor);
  const member = draft.members[index];
  if (!member) return null;
  const saved = view.members.find((m) => m.vendor === vendor);
  const label = vendorLabel(t, view.category, vendor);
  const editable = api.editable;
  const leads = view.leader?.vendor === vendor ? view.leader : undefined;

  return (
    <div
      className="space-y-1 rounded-md border bg-background p-2"
      data-testid={`routing-member-${key}-${vendor}`}
      data-enabled={member.enabled}
      data-serving={saved?.serving ?? false}
      data-leader={leads ? (leads.over === '*' ? 'first' : 'before') : undefined}
    >
      <p className="text-xs">
        <span className="font-medium">
          {t('providers.routing.title', { capability: capabilityLabel(t, view) })}
        </span>{' '}
        <span className="text-muted-foreground" data-testid={`routing-position-${key}-${vendor}`}>
          {t('providers.routing.position', { n: index + 1, total: draft.members.length })}
        </span>
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex min-h-11 flex-1 items-center gap-2 text-xs">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={member.enabled}
            disabled={!editable || !api.canParticipate(vendor, !member.enabled)}
            onChange={(e) => api.setParticipates(vendor, e.target.checked)}
            aria-label={t('providers.routing.participatesLabel', { name: label })}
            data-testid={`routing-participates-${key}-${vendor}`}
          />
          <span aria-hidden>{t('providers.routing.participates')}</span>
        </label>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-11 w-11"
          disabled={!editable || !api.canMove(vendor, -1)}
          onClick={() => api.move(vendor, -1)}
          aria-label={t('providers.routing.moveUp', { name: label })}
          data-testid={`routing-up-${key}-${vendor}`}
        >
          <ArrowUp className="h-4 w-4" aria-hidden />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-11 w-11"
          disabled={!editable || !api.canMove(vendor, 1)}
          onClick={() => api.move(vendor, 1)}
          aria-label={t('providers.routing.moveDown', { name: label })}
          data-testid={`routing-down-${key}-${vendor}`}
        >
          <ArrowDown className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      {leads ? (
        <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground" data-testid={`routing-locked-${key}-${vendor}`}>
          <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          <span>
            <span className="font-medium text-foreground">{t('providers.routing.leader.badge')}.</span>{' '}
            {leaderWhy(t, view, leads)}
          </span>
        </p>
      ) : null}
      {saved && !saved.serving ? (
        <p className="text-[11px] text-amber-700 dark:text-amber-400" data-testid={`routing-not-serving-${key}-${vendor}`}>
          {t('providers.routing.notServing')}
        </p>
      ) : null}
    </div>
  );
}
